import React, { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Download, KeyRound } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card } from "./ui/card";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

function BackupCodesPanel({ codes, onDone }) {
    const download = () => {
        const text = `IH PEC Guard — Codici di backup MFA\nGenerati il ${new Date().toLocaleString("it-IT")}\n\n${codes.join("\n")}\n\nOgni codice è utilizzabile una sola volta. Conservali in un luogo sicuro.\n`;
        const blob = new Blob([text], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "ihpg_backup_codes.txt";
        a.click(); URL.revokeObjectURL(url);
    };
    return (
        <div className="mt-4 bg-[#F5F7FB] border border-[#DCE3EE] rounded-lg p-4" data-testid="mfa-backup-codes-panel">
            <div className="text-sm font-semibold text-[#172033] mb-2">I tuoi codici di backup</div>
            <p className="text-xs text-[#64748B] mb-3">Salva questi 10 codici in un luogo sicuro. Ognuno è utilizzabile una sola volta.</p>
            <div className="grid grid-cols-2 gap-2 font-mono text-sm mb-3" data-testid="mfa-backup-codes-list">
                {codes.map((c, i) => (
                    <div key={i} data-testid={`mfa-backup-code-${i}`} className="bg-white border border-[#DCE3EE] rounded-md px-3 py-1.5 text-center">{c}</div>
                ))}
            </div>
            <div className="flex gap-2">
                <Button variant="outline" data-testid="mfa-backup-download" onClick={download} className="gap-2">
                    <Download className="w-4 h-4" /> Scarica .txt
                </Button>
                {onDone && (
                    <Button data-testid="mfa-backup-done" onClick={onDone} className="bg-[#2563EB] text-white">Ho salvato i codici</Button>
                )}
            </div>
        </div>
    );
}

export default function MfaCard() {
    const { user, refreshMe } = useAuth();
    const mfaEnabled = !!user?.mfa_enabled;
    const [setupData, setSetupData] = useState(null); // {secret, qr_code_data_url, backup_codes}
    const [verifyCode, setVerifyCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [disableForm, setDisableForm] = useState(null); // {password, code}
    const [regenForm, setRegenForm] = useState(null);
    const [newCodes, setNewCodes] = useState(null);

    const startSetup = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/auth/mfa/setup");
            setSetupData(data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore setup"); }
        finally { setBusy(false); }
    };

    const verify = async () => {
        if (!/^\d{6}$/.test(verifyCode.replace(/\s/g, ""))) { toast.error("Inserisci un codice a 6 cifre"); return; }
        setBusy(true);
        try {
            await api.post("/auth/mfa/verify", { code: verifyCode.replace(/\s/g, "") });
            toast.success("MFA attivato correttamente");
            setSetupData(null); setVerifyCode("");
            await refreshMe();
        } catch (e) { toast.error(e?.response?.data?.detail || "Codice non valido"); }
        finally { setBusy(false); }
    };

    const doDisable = async () => {
        if (!disableForm?.password || !disableForm?.code) { toast.error("Password e codice obbligatori"); return; }
        setBusy(true);
        try {
            await api.post("/auth/mfa/disable", disableForm);
            toast.success("MFA disattivato");
            setDisableForm(null);
            await refreshMe();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
        finally { setBusy(false); }
    };

    const doRegenerate = async () => {
        if (!regenForm?.password || !regenForm?.code) { toast.error("Password e codice obbligatori"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/auth/mfa/regenerate-backup-codes", regenForm);
            setNewCodes(data.backup_codes || []);
            setRegenForm(null);
            toast.success("Codici di backup rigenerati");
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
        finally { setBusy(false); }
    };

    return (
        <Card className="p-6 border-[#DCE3EE]">
            <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#16A34A]/10 flex items-center justify-center shrink-0">
                    <ShieldCheck className="w-5 h-5 text-[#16A34A]" />
                </div>
                <div className="flex-1">
                    <h2 className="text-lg font-semibold text-[#172033]">Autenticazione a due fattori (MFA)</h2>
                    <p className="text-sm text-[#64748B] mt-1">Aggiungi un livello di sicurezza al tuo account con TOTP (Google Authenticator, 1Password, Authy…).</p>
                    <div className="mt-2 text-xs">
                        {mfaEnabled ? (
                            <span data-testid="mfa-status-badge" className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#16A34A]/10 text-[#16A34A] font-medium">
                                ● Attivo{user?.mfa_enabled_at ? ` dal ${new Date(user.mfa_enabled_at).toLocaleDateString("it-IT")}` : ""}
                            </span>
                        ) : (
                            <span data-testid="mfa-status-badge" className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-700 font-medium">Non attivo</span>
                        )}
                    </div>
                </div>
            </div>

            {!mfaEnabled && !setupData && (
                <Button data-testid="mfa-enable-btn" onClick={startSetup} disabled={busy}
                        className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
                    Attiva autenticazione a due fattori
                </Button>
            )}

            {setupData && (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col items-center bg-[#F5F7FB] rounded-lg p-4">
                            <div className="text-xs text-[#64748B] mb-2">Scansiona con la tua app</div>
                            <img data-testid="mfa-qr-image" src={setupData.qr_code_data_url} alt="QR MFA" className="w-44 h-44 border border-[#DCE3EE] bg-white rounded" />
                            <div className="text-[10px] text-[#64748B] mt-2">o inserisci manualmente il codice:</div>
                            <div data-testid="mfa-secret" className="mt-1 font-mono text-xs bg-white border border-[#DCE3EE] rounded px-2 py-1 select-all">
                                {setupData.secret}
                            </div>
                        </div>
                        <div>
                            <Label>Codice a 6 cifre</Label>
                            <Input data-testid="mfa-verify-input" value={verifyCode} onChange={(e) => setVerifyCode(e.target.value)}
                                   placeholder="123 456" className="mt-1 h-11 font-mono text-lg text-center tracking-widest" autoFocus />
                            <Button data-testid="mfa-verify-btn" onClick={verify} disabled={busy} className="mt-3 w-full bg-[#2563EB] text-white">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Conferma e attiva"}
                            </Button>
                            <button onClick={() => { setSetupData(null); setVerifyCode(""); }} className="mt-2 w-full text-xs text-[#64748B] hover:text-[#172033]">Annulla</button>
                        </div>
                    </div>
                    <BackupCodesPanel codes={setupData.backup_codes} />
                </div>
            )}

            {mfaEnabled && !setupData && (
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" data-testid="mfa-regenerate-btn" onClick={() => setRegenForm({ password: "", code: "" })}>
                        Rigenera codici di backup
                    </Button>
                    <Button variant="outline" data-testid="mfa-disable-btn" onClick={() => setDisableForm({ password: "", code: "" })}
                            className="text-[#DC2626] border-[#DC2626]/30 hover:bg-[#DC2626]/5">
                        Disattiva MFA
                    </Button>
                </div>
            )}

            {disableForm && (
                <Modal title="Disattiva MFA" onClose={() => setDisableForm(null)}>
                    <div className="space-y-3">
                        <div>
                            <Label>Password</Label>
                            <Input type="password" data-testid="mfa-disable-password" value={disableForm.password}
                                   onChange={(e) => setDisableForm({ ...disableForm, password: e.target.value })} />
                        </div>
                        <div>
                            <Label>Codice TOTP (o backup)</Label>
                            <Input data-testid="mfa-disable-code" value={disableForm.code}
                                   onChange={(e) => setDisableForm({ ...disableForm, code: e.target.value })}
                                   className="font-mono" />
                        </div>
                        <Button data-testid="mfa-disable-confirm" onClick={doDisable} disabled={busy} className="w-full bg-[#DC2626] text-white">
                            Conferma disattivazione
                        </Button>
                    </div>
                </Modal>
            )}

            {regenForm && (
                <Modal title="Rigenera codici di backup" onClose={() => setRegenForm(null)}>
                    <div className="space-y-3">
                        <p className="text-xs text-[#64748B]">I vecchi codici verranno invalidati.</p>
                        <div>
                            <Label>Password</Label>
                            <Input type="password" data-testid="mfa-regen-password" value={regenForm.password}
                                   onChange={(e) => setRegenForm({ ...regenForm, password: e.target.value })} />
                        </div>
                        <div>
                            <Label>Codice TOTP corrente</Label>
                            <Input data-testid="mfa-regen-code" value={regenForm.code}
                                   onChange={(e) => setRegenForm({ ...regenForm, code: e.target.value })}
                                   className="font-mono" />
                        </div>
                        <Button data-testid="mfa-regen-confirm" onClick={doRegenerate} disabled={busy} className="w-full bg-[#2563EB] text-white">
                            Rigenera
                        </Button>
                    </div>
                </Modal>
            )}

            {newCodes && (
                <Modal title="Nuovi codici di backup" onClose={() => setNewCodes(null)}>
                    <BackupCodesPanel codes={newCodes} onDone={() => setNewCodes(null)} />
                </Modal>
            )}
        </Card>
    );
}

function Modal({ children, title, onClose }) {
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-[#172033]">{title}</h4>
                    <button onClick={onClose} className="text-[#64748B]">✕</button>
                </div>
                {children}
            </div>
        </div>
    );
}
